import React, { useEffect, useState } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import Button from '../../components/common/Button';
import {  ActivityCategoryDTO, ProvinceDTO, QuickEventDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useProvinces } from '../../hooks/useProvinceApi';
import { useActivityCategories } from '../../hooks/useActivityCategoryApi';

interface QuickEventFormProps {
  initialValues: QuickEventDTO;
  onSubmit: (data: QuickEventDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  categories: {
    id: 'categories',
    title: 'Categories',
  },
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  location: {
    id: 'location',
    title: 'Location',
  },
  dates: {
    id: 'dates',
    title: 'Dates',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
} as const;

const QuickEventForm: React.FC<QuickEventFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<QuickEventDTO>(initialValues);

  const { data: activityCategories = [], isLoading: isLoadingActivityCategories } = useActivityCategories();
  const [activityCategoriesMap, setActivityCategoriesMap] = useState<Map<string, ActivityCategoryDTO>>(new Map());

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    provinceId: [{ required: true, message: 'Province is required' }],
    town: [{ required: true, message: 'Town is required' }],
    location: [{ required: true, message: 'Location is required' }],
  };

  const { errors, validate } =
    useFormValidation<QuickEventDTO>(validationRules);

  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();

  useEffect(() => {
    // Build activityCategoriesMap from cached data
    const categoriesMapping = new Map(
      activityCategories
        .filter(c => c.id)
        .map(c => [c.id as string, c])
    );
    setActivityCategoriesMap(categoriesMapping);
  }, [activityCategories]);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.categories.id}
        title={FORM_SECTIONS.categories.title}
      >
        <FormField
          id="activityCategories"
          label="Categories"
          type="multiselect"
          value={(formData.activityCategories || []).map(c => c.id as string)}
          onChange={(e) => {
            const selectedIds = Array.isArray(e.target.value) 
              ? e.target.value 
              : [e.target.value];
            
            const selectedCategories = selectedIds
              .map(id => activityCategoriesMap.get(id))
              .filter((c): c is ActivityCategoryDTO => c !== undefined);

            handleChange(
              {
                target: {
                  id: 'activityCategories',
                  value: selectedCategories
                },
              } as any,
              setFormData,
            );
          }}
          options={activityCategories
            .filter(c => c.id && c.activityType?.title)
            .map(c => ({
              value: c.id as string,
              label: `${c.activityType?.title} - ${c.title || ''}`,
            }))}
          placeholder="Select categories..."
          helper="Select one or more acticity categories for this quick event"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
          helper="Use {{date}} to add today's date and {{year}} for this year"
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.location.id}
        title={FORM_SECTIONS.location.title}
      >
        <FormField
          id="provinceId"
          label="Province"
          type="dropdown"
          value={formData.provinceId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Province"
          required={true}
          error={errors.provinceId}
          options={(provinces || []).map((type) => ({
            value: type.id ?? '',
            label: type.title ?? '',
          }))}
        />
        <FormField
          id="town"
          label="Town"
          type="text"
          value={formData.town || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Town"
          required={true}
          error={errors.town}
        />
        <FormField
          id="location"
          label="Location"
          type="text"
          value={formData.location || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Location"
          required={true}
          error={errors.location}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.dates.id}
        title={FORM_SECTIONS.dates.title}
      >
        <FormField
          id="dateStart"
          label="Date Start (Optional)"
          type="date"
          value={formData.dateStart || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Date Start"
          required={false}
          error={errors.dateStart}
        />
        <FormField
          id="dateEnd"
          label="Date End (Optional)"
          type="date"
          value={formData.dateEnd || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Date End"
          required={false}
          error={errors.dateEnd}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active."
        />
      </FormSection>
    </Form>
  );
};

export default QuickEventForm;
